import { detectorIds } from '../domain/analysis';
import { normalizeMediaUrl } from '../domain/media';
import { translateError } from '../i18n/errors';

import type { AnalysisConfig, DetectorId } from '../domain/analysis';
import type { MediaKind, MediaSource } from '../domain/media';
import type { Translate } from '../i18n/types';

interface StageInput {
    detector?: DetectorId;
    matching?: boolean;
    referenceUrl?: string;
    referenceKind?: MediaKind;
    haystackUrl: string;
    haystackKind?: MediaKind;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseKind(value: unknown, fallback: MediaKind, translate: Translate): MediaKind {
    if (value === undefined) return fallback;
    if (value === 'image' || value === 'video') return value;
    throw new Error(translate('webmcp.error.kind'));
}

function parseStageInput(input: unknown, translate: Translate): StageInput {
    if (!isRecord(input) || typeof input.haystackUrl !== 'string') {
        throw new Error(translate('webmcp.error.haystack'));
    }
    if (input.detector !== undefined && !detectorIds.includes(input.detector as DetectorId)) {
        throw new Error(translate('webmcp.error.detector'));
    }
    if (input.matching === true && typeof input.referenceUrl !== 'string') {
        throw new Error(translate('webmcp.error.reference'));
    }
    return {
        detector: input.detector as DetectorId | undefined,
        matching: typeof input.matching === 'boolean' ? input.matching : undefined,
        referenceUrl: typeof input.referenceUrl === 'string' ? input.referenceUrl : undefined,
        referenceKind: parseKind(input.referenceKind, 'image', translate),
        haystackUrl: input.haystackUrl,
        haystackKind: parseKind(input.haystackKind, 'image', translate),
    };
}

function urlSource(url: string, kind: MediaKind, translate: Translate): MediaSource {
    let normalized: string;
    try {
        normalized = normalizeMediaUrl(url);
    } catch (error) {
        throw new Error(translateError(error, translate, 'error.invalidUrl'));
    }
    return {
        id: crypto.randomUUID(),
        origin: 'url',
        kind,
        name: new URL(normalized).hostname,
        url: normalized,
    };
}

export function registerFaceAnalysisTool({
    getConfig,
    setConfig,
    setMedia,
    invalidate,
    translate,
}: {
    getConfig: () => AnalysisConfig;
    setConfig: (config: AnalysisConfig) => void;
    setMedia: (media: { reference: MediaSource | null; haystack: MediaSource }) => void;
    invalidate: () => void;
    translate: Translate;
}): () => void {
    if (document.modelContext?.registerTool === undefined) return () => undefined;
    const lifecycle = new AbortController();
    const registration = document.modelContext.registerTool(
        {
            name: 'stage_face_analysis',
            title: translate('webmcp.title'),
            description: translate('webmcp.description'),
            inputSchema: {
                type: 'object',
                properties: {
                    detector: {
                        enum: ['tinyFaceDetector', 'ssdMobilenetv1', 'mtcnn', 'tinyYolov2'],
                    },
                    matching: { type: 'boolean' },
                    referenceUrl: { type: 'string', format: 'uri' },
                    referenceKind: { enum: ['image', 'video'] },
                    haystackUrl: { type: 'string', format: 'uri' },
                    haystackKind: { enum: ['image', 'video'] },
                },
                required: ['haystackUrl'],
                additionalProperties: false,
            },
            annotations: { readOnlyHint: false, untrustedContentHint: true },
            async execute(input) {
                const parsed = parseStageInput(input, translate);
                const config = getConfig();
                const matching = parsed.matching ?? config.features.matching;
                setConfig({
                    ...config,
                    detector: parsed.detector ?? config.detector,
                    features: { ...config.features, matching },
                });
                setMedia({
                    reference:
                        matching && parsed.referenceUrl !== undefined
                            ? urlSource(
                                  parsed.referenceUrl,
                                  parsed.referenceKind ?? 'image',
                                  translate
                              )
                            : null,
                    haystack: urlSource(
                        parsed.haystackUrl,
                        parsed.haystackKind ?? 'image',
                        translate
                    ),
                });
                invalidate();
                await new Promise<void>((resolve) =>
                    requestAnimationFrame(() => {
                        resolve();
                    })
                );
                return { staged: true, detector: parsed.detector ?? config.detector, matching };
            },
        },
        { signal: lifecycle.signal }
    );
    void Promise.resolve(registration).catch(() => {
        lifecycle.abort();
    });
    return () => {
        lifecycle.abort();
    };
}
